import { Controller } from 'stimulus';
import { updateQuantity } from '@/helpers/ecommerce/cartHelpers';
import debounce from '@/helpers/debounce';

function getCartPage() {
  return $.ajax({
    url: window.app.api.cart,
  });
}

function parseCartPage(res) {
  return $(res);
}

function getTextFromPage($elements, selector) {
  const $items = $elements.find(selector);

  if (!$items.length) {
    return null;
  }

  return $items.get(0).innerHTML.trim();
}

function extractFromCartPage($elements) {
  const prices = {};
  const pricesPerPiece = {};
  const pricesForPallet = {};

  $elements.find('[data-target="cart.cartItemPriceOutput"]').each((index, element) => {
    prices[element.getAttribute('data-cart-item-id')] = element.innerHTML.trim();
  });

  $elements.find('[data-target="cart.cartItemPricePerPieceOutput"]').each((index, element) => {
    pricesPerPiece[element.getAttribute('data-cart-item-id')] = element.innerHTML.trim();
  });

  $elements.find('[data-target="cart.cartItemPriceForPalletOutput"]').each((index, element) => {
    pricesForPallet[element.getAttribute('data-cart-item-id')] = element.innerHTML.trim();
  });

  return {
    subTotal: getTextFromPage($elements, '[data-target~="cart.subTotal"]'),
    shippingCosts: getTextFromPage($elements, '[data-target~="cart.shippingCosts"]'),
    couponValue: getTextFromPage($elements, '[data-target~="cart.couponValue"]'),
    totalTax: getTextFromPage($elements, '[data-target~="cart.totalTax"]'),
    total: getTextFromPage($elements, '[data-target~="cart.total"]'),
    hasSurcharge: $elements.find('[data-target~="cart.hasSurcharge"]')?.val() ?? '0',
    prices,
    pricesPerPiece,
    pricesForPallet,
  };
}

class Cart extends Controller {
  static removeItem(item) {
    if (item && item.parentNode) {
      item.parentNode.removeChild(item);
    }
  }

  connect() {
    this.updatePricesDebounced = debounce(this.updatePrices.bind(this), 20);
    this.changeQuantityDebounced = debounce(this.changeQuantity.bind(this), 20);

    this.cartItemTargets.forEach((cartItem) => {
      const quantityInput = cartItem.querySelector('[data-target~="cart.quantityInput"]');

      if (quantityInput) {
        this.updateMaximumQuantityForCartItem(cartItem, quantityInput);
      }
    });
  }

  onQuantityChange({ target }) {
    this.changeQuantityDebounced(target);
  }

  async changeQuantity(target) {
    const item = target.closest('[data-target="cart.cartItem"]');

    if (!item) return;

    const quantity = target.value;
    const itemId = item.getAttribute('data-id');
    const palletType = item.getAttribute('data-pallet-type');

    if (parseInt(quantity, 10) === 0) {
      Cart.removeItem(item);
    }

    this.updateMaximumQuantityForCartItem(item, target);

    try {
      // Update the quantity first
      await updateQuantity(itemId, quantity, palletType);

      // Get the updated cart state
      const cartPageHtml = await getCartPage();
      const $cartPage = parseCartPage(cartPageHtml);
      const cartSpecifications = extractFromCartPage($cartPage);

      // Handle surcharge items
      await this.handleSurchargeUpdate(cartSpecifications);

      // Update all cart displays
      await this.updateCartDisplay(cartSpecifications);
    } catch (error) {
      console.error('Error updating cart:', error);
      this.updatePricesDebounced();
    }
  }

  async handleSurchargeUpdate(cartSpecifications) {
    const hasSurcharge = cartSpecifications.hasSurcharge === '1';
    const currentHasSurcharge = this.hasSurchargeTarget?.value === '1';

    // If surcharge status changed, we need to refresh the cart items
    if (hasSurcharge !== currentHasSurcharge) {
      // Get the cart items container
      const cartItemsContainer = this.cartItemTargets[0]?.parentElement;

      if (cartItemsContainer) {
        const cartPageHtml = await getCartPage();
        const $cartPage = parseCartPage(cartPageHtml);
        const $newCartItems = $cartPage.find('[data-target="cart.cartItem"]').parent();

        // Replace the cart items container with the new one
        cartItemsContainer.innerHTML = $newCartItems.html();

        // Re-initialize quantity inputs for the new items
        this.cartItemTargets.forEach((cartItem) => {
          const quantityInput = cartItem.querySelector('[data-target~="cart.quantityInput"]');
          if (quantityInput) {
            this.updateMaximumQuantityForCartItem(cartItem, quantityInput);
          }
        });
      }
    } else {
      // If no status change, just handle visibility of existing items
      this.cartItemTargets.forEach((item) => {
        const productIdentifier = item.getAttribute('data-product-identifier');
        if (productIdentifier === 'surcharge') {
          if (!hasSurcharge) {
            Cart.removeItem(item);
          }
        }
      });
    }

    // Update surcharge visibility
    if (this.hasShippingCostParentTarget) {
      this.shippingCostParentTarget.style.display = hasSurcharge ? 'none' : '';
    }

    if (this.hasSurchargeTarget) {
      this.hasSurchargeTarget.value = hasSurcharge ? '1' : '0';
    }
  }

  async updateCartDisplay(cartSpecifications) {
    // Update prices
    if (this.hasSubTotalTarget) {
      this.subTotalTarget.innerHTML = cartSpecifications.subTotal;
    }

    if (this.hasShippingCostsTarget) {
      this.shippingCostsTarget.innerHTML = cartSpecifications.shippingCosts;
    }

    if (this.hasTotalTaxTarget) {
      this.totalTaxTarget.innerHTML = cartSpecifications.totalTax;
    }

    if (this.hasTotalTarget) {
      this.totalTarget.innerHTML = cartSpecifications.total;
    }

    if (this.hasCouponValueTarget) {
      this.couponValueTarget.innerHTML = cartSpecifications.couponValue;
    }

    // Update item prices
    if (typeof cartSpecifications.prices !== 'undefined') {
      this.cartItemPriceOutputTargets.forEach((element) => {
        const id = element.getAttribute('data-cart-item-id');
        element.innerHTML = cartSpecifications.prices[id] ?? '-';
      });
    }

    if (typeof cartSpecifications.pricesPerPiece !== 'undefined') {
      this.cartItemPricePerPieceOutputTargets.forEach((element) => {
        const id = element.getAttribute('data-cart-item-id');
        element.innerHTML = cartSpecifications.pricesPerPiece[id] ?? '-';
      });
    }

    if (typeof cartSpecifications.pricesForPallet !== 'undefined') {
      this.cartItemPriceForPalletOutputTargets.forEach((element) => {
        const id = element.getAttribute('data-cart-item-id');
        element.innerHTML = cartSpecifications.pricesForPallet[id] ?? '-';
      });
    }

    // Update all cart summary controllers
    document.querySelectorAll('[data-controller~="cart-summary"]').forEach((el) => {
      const controller = this.application.getControllerForElementAndIdentifier(el, 'cart-summary');
      if (controller) {
        controller.recalculate();
      }
    });
  }

  updatePrices() {
    getCartPage()
      .then(parseCartPage)
      .then(extractFromCartPage)
      .then(async (cartSpecifications) => {
        await this.handleSurchargeUpdate(cartSpecifications);
        await this.updateCartDisplay(cartSpecifications);
      });
  }

  updateMaximumQuantityForCartItem(item, quantityInput) {
    const { palletType, amountInStock, priceSchemes } = item.dataset;
    if (!priceSchemes) return;

    const priceSchemesArr = Object.values(JSON.parse(priceSchemes));
    priceSchemesArr.reverse();

    const priceScheme = priceSchemesArr.find((scheme) => palletType === scheme.pallet_type);
    if (!priceScheme) return;

    const { pallet_quantity: palletQuantity } = priceScheme;
    quantityInput.max = Math.floor(amountInStock / palletQuantity);
  }
}

Cart.targets = [
  'quantityInput',
  'cartItem',
  'subTotal',
  'totalTax',
  'total',
  'shippingCosts',
  'couponValue',
  'cartItemPricePerPieceOutput',
  'cartItemPriceOutput',
  'cartItemPriceForPalletOutput',
  'shippingCostParent',
  'hasSurcharge',
];

export default Cart;
